import { useState, useEffect, useCallback, useRef } from "react";

const px = (h) => (h > 0 ? `${h}px` : "");

const applySidebarOffset = (h) => {
  const sidebar = document.querySelector(".navbar-vertical");
  if (!sidebar) return;
  sidebar.style.bottom = px(h);
  // On mobile (<768px) the sidebar is not position:fixed — it scrolls with the page.
  // When the banner is visible, make it sticky so the nav stays accessible.
  if (globalThis.matchMedia("(min-width: 768px)").matches) return;
  sidebar.style.position = h > 0 ? "sticky" : "";
  sidebar.style.top = h > 0 ? "0" : "";
  sidebar.style.zIndex = h > 0 ? "1050" : "";
};

const useBannerOffset = (bannerRef, visible) => {
  const [bannerHeight, setBannerHeight] = useState(0);

  const applyOffset = useCallback((h) => {
    applySidebarOffset(h);
    document.body.style.paddingBottom = px(h);
    document.documentElement.style.setProperty("--banner-height", h > 0 ? `${h}px` : "0px");
    globalThis.dispatchEvent(new CustomEvent("bannerresize", { detail: { height: h } }));
    setBannerHeight(h);
  }, []);

  // The offset is global: one --banner-height, one body padding, one sidebar.
  // More than one banner uses this hook (the incident banner at the app root,
  // the maintenance banner inside Standard.jsx), and only one is ever shown.
  // Without this guard a hidden banner would reset the offset to zero on mount
  // and wipe the value the visible one had just set, leaving that banner
  // covering the content it was supposed to make room for.
  const ownsOffset = useRef(false);

  const releaseOffset = useCallback(() => {
    if (!ownsOffset.current) return;
    ownsOffset.current = false;
    applyOffset(0);
  }, [applyOffset]);

  const claimOffset = useCallback(
    (h) => {
      ownsOffset.current = true;
      applyOffset(h);
    },
    [applyOffset]
  );

  useEffect(() => {
    if (!visible) {
      releaseOffset();
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      claimOffset(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      releaseOffset();
    };
  }, [visible, claimOffset, releaseOffset]);

  // The incident banner mounts at the app root, before Standard.jsx has rendered
  // the sidebar, so the pass above finds no .navbar-vertical and the banner ends
  // up covering the bottom of the nav once it appears. Wait for it and offset it
  // then. Nothing to do for a banner that mounts inside a layout, where the
  // sidebar is already there.
  useEffect(() => {
    if (!visible || bannerHeight <= 0) return;
    if (document.querySelector(".navbar-vertical")) return;

    const observer = new MutationObserver(() => {
      if (!document.querySelector(".navbar-vertical")) return;
      applySidebarOffset(bannerHeight);
      observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [visible, bannerHeight]);

  return bannerHeight;
};

export default useBannerOffset;
